import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
	type ICreateObserverApplicationRequest,
	type IElectionObserverLinks,
	type IPartnerGuid,
	type Organisation,
} from '@/interface/IObserver';
import { organisationFromPartnerLink } from '@/utils/volunteerRole';

import { COREBASEURL } from '../../constants/global';

// RTK Query slice for the Election Observer lane's read endpoints. Scoped to the
// new observer endpoints only — the party-side data still flows through the
// legacy spread-reducer thunks and will be migrated gradually. Endpoints are a
// backend contract assumption (see docs/PRD-observer-role.md).
export const observerApi = createApi({
	reducerPath: 'observerApi',
	baseQuery: fetchBaseQuery({ baseUrl: COREBASEURL }),
	tagTypes: ['ObserverLinks', 'Partner'],
	endpoints: (builder) => ({
		// Developmental partners registered for an election, adapted to Organisation[].
		getElectionObserverLinks: builder.query<Organisation[], string>({
			query: (electionId) => `ElectionObserver/get-links-for-election/${electionId}`,
			transformResponse: (response: IElectionObserverLinks) => (response?.data ?? []).map(organisationFromPartnerLink),
			providesTags: ['ObserverLinks'],
		}),
		// Resolves a developmental partner from its URL GUID (mirrors Party/guid).
		getPartnerByGuid: builder.query<IPartnerGuid, string>({
			query: (guid) => `ElectionObserver/guid/${guid}`,
			providesTags: ['Partner'],
		}),
		createObserverApplication: builder.mutation<Record<string, unknown>, ICreateObserverApplicationRequest>({
			query: (body) => ({
				method: 'POST',
				url: 'ObserverApplication',
				body,
			}),
		}),
	}),
});

export const { useGetElectionObserverLinksQuery, useGetPartnerByGuidQuery, useCreateObserverApplicationMutation } = observerApi;
